import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { DeviationTypes } from '../../types/deviation';
import { toMediaSrcFile } from './helpers';
const defaultFullview = {
    type: 'unknown',
    src: '',
    width: 1000,
    height: 1000,
    isDefault: true
};
class MediaHandler {
    isGif(deviation) {
        if (deviation && deviation.isGif) {
            return true;
        }
        if (!deviation.media || !Array.isArray(deviation.media.types)) {
            return false;
        }
        return deviation.media.types.find((file)=>file.t === 'gif') ? true : false;
    }
    isPng(deviation) {
        if (!deviation.media || !deviation.media.types) {
            return false;
        }
        return deviation.media.types.find((file)=>{
            var _file_c;
            return (_file_c = file.c) === null || _file_c === void 0 ? void 0 : _file_c.endsWith('.png');
        }) ? true : false;
    }
    getDeviationType(deviation) {
        if (!deviation) {
            return DeviationTypes.UNKNOWN;
        }
        // New type handling backend now defines
        if (deviation.type) {
            // TODO: we removed motionbook on the frontend, clean this up once the back-end stops sending it
            if (deviation.type !== 'motionbook') {
                return deviation.type;
            }
        }
        // Legacy duck typing below
        // polls
        if (deviation.poll) {
            return DeviationTypes.POLL;
        }
        // commission
        if (deviation.commission) {
            return DeviationTypes.COMMISSION;
        }
        // status
        if (deviation.status) {
            return DeviationTypes.STATUS;
        }
        // Freeform Text (Journal - Status - Literature)
        if (deviation.textContent) {
            if (deviation.isJournal) {
                return DeviationTypes.JOURNAL;
            }
            return DeviationTypes.LITERATURE;
        }
        // From here we should have some sort of media
        if (!deviation.media || !deviation.media.types || deviation.media.types.length === 0) {
            return DeviationTypes.UNKNOWN;
        }
        const fileTypes = {};
        for (const file of deviation.media.types){
            fileTypes[file.t] = true;
        }
        // Video
        if (deviation.isVideo || fileTypes.video) {
            return DeviationTypes.FILM;
        }
        // PDF
        if (fileTypes.pdf) {
            return DeviationTypes.PDF;
        }
        // GIF
        if (deviation.isGif || fileTypes.gif) {
            return DeviationTypes.IMAGE;
        }
        return DeviationTypes.IMAGE;
    }
    getLargestFullview(deviation) {
        const type = this.getDeviationType(deviation);
        if (type === DeviationTypes.JOURNAL || type === DeviationTypes.LITERATURE) {
            return defaultFullview;
        }
        return this._getLargestFullview(deviation);
    }
    getThumbDimensions(deviation) {
        const fv = this._getLargestFullview(deviation);
        const type = this.getDeviationType(deviation);
        if (type === DeviationTypes.TIER) {
            return {
                width: 1840,
                height: 1000
            };
        }
        if (!fv || !fv.width || !fv.height || [
            DeviationTypes.JOURNAL,
            DeviationTypes.LITERATURE,
            DeviationTypes.STATUS,
            DeviationTypes.COMMISSION
        ].includes(type)) {
            return {
                width: 1000,
                height: 1000
            };
        }
        return {
            width: fv.width,
            height: fv.height
        };
    }
    getSmallestThumb(deviation, options) {
        const thumbs = this.getThumbSet(deviation, options);
        if (!thumbs) {
            return;
        }
        const small = this._getThumbsSortedSmallToBig(thumbs);
        return small[0];
    }
    getSmallestThumbThatFullyCovers(deviation, width, height, fallback, options) {
        const thumbs = this.getThumbSet(deviation, options);
        if (!thumbs) {
            return;
        }
        // Allow slightly smaller than the requested block, the scaling
        // rules allow for 25%~ smaller to be scaled up so make this match
        // those. We force portraits to match the width.
        let allowedWidth = width; // * 0.85;
        const allowedHeight = height; // * 0.85;
        if (width < height) {
            allowedWidth = width;
        }
        const smallest = this._getThumbsSortedSmallToBig(thumbs).find((img)=>{
            const { width: imgWidth = 0, height: imgHeight = 0 } = img;
            return imgWidth >= allowedWidth && imgHeight >= allowedHeight;
        });
        // Allow caller to determine the fallback
        if (!smallest && fallback) {
            return fallback(thumbs);
        }
        return smallest;
    }
    getSmallestThumbForArtStage(deviation, width, height) {
        // GIF has to use the fullview
        if (this.isGif(deviation)) {
            return this.getFullviewForSize(deviation, width, height);
        }
        // For thumbs as its meant to be a fullview we avoid crops and fits
        const thumbsAll = this.getThumbSet(deviation, {
            allowPreview: true
        });
        const thumbs = thumbsAll.filter((f)=>f.src.includes('/fill/'));
        if (!thumbs) {
            return;
        }
        // Get the smallest thumb this is bigger than the stage in any direction
        const smallest = this._getThumbsSortedSmallToBig(thumbs).find((img)=>{
            const { width: imgWidth = 0, height: imgHeight = 0 } = img;
            return imgWidth >= width || imgHeight >= height;
        });
        // Allow caller to determine the fallback
        if (!smallest) {
            return this.getFullviewForSize(deviation, width, height);
        }
        return smallest;
    }
    _getThumbsSortedSmallToBig(thumbs) {
        if (!thumbs) {
            return [];
        }
        return thumbs.sort((a, b)=>{
            const asc = (a.width || 0) * (a.height || 0);
            const bsc = (b.width || 0) * (b.height || 0);
            if (asc === bsc) {
                return 0;
            }
            return asc < bsc ? -1 : 1;
        });
    }
    getFullviewForSize(deviation, width, height) {
        const fullviews = this._getFullviews(deviation);
        if (!fullviews.length) {
            return;
        }
        // sort by resolution small to big
        fullviews.sort((a, b)=>this._getFilePixelSize(a) - this._getFilePixelSize(b));
        // find smallest fitting file
        if (width && height) {
            const file = fullviews.find((f)=>{
                if (!f.w || !f.h) {
                    return false;
                }
                return f.w >= width && f.h >= height;
            });
            if (file) {
                return toMediaSrcFile(deviation, file);
            }
        }
        // if no fitting file was found return largest one
        return toMediaSrcFile(deviation, fullviews[fullviews.length - 1]);
    }
    _getFullviews(deviation) {
        if (!deviation || !deviation.media || !deviation.media.types) {
            return [];
        }
        const files = deviation && deviation.media && deviation.media.types;
        // for gif images only the gif file is a valid fullview
        if (this.isGif(deviation)) {
            return files.filter((file)=>file && /^fullview/.test(file.t));
        }
        return files.filter((file)=>file && /^fullview|preview|original/.test(file.t));
    }
    _getFilePixelSize(file) {
        return (file.w || 0) * (file.h || 0);
    }
    _mightBeCompressedFilesList(deviation) {
        if (!deviation || !deviation.media || !deviation.media.types) {
            return false;
        }
        // Assume single file whatever it is to be safe
        if (deviation.media.types.length === 1) {
            return true;
        }
        const types = deviation.media.types.map((f)=>f.t);
        // social_preview bug T38231
        if (types.length === 2 && types.includes('fullview') && types.includes('social_preview')) {
            return true;
        }
        return false;
    }
    getDeviationFile(deviation, fileType) {
        const types = deviation && deviation.media && deviation.media.types;
        const item = types && types.find((f)=>f.t === fileType);
        if (item) {
            return toMediaSrcFile(deviation, item);
        }
        return undefined;
    }
    getThumbSet(deviation, options) {
        const thumbTypes = this._getFilteredMediaTypes(deviation, options);
        return thumbTypes.map((file)=>toMediaSrcFile(deviation, file));
    }
    getMediaByType(deviation, fileType) {
        if (!deviation || !deviation.media || !deviation.media.types) {
            return [];
        }
        return deviation.media.types.filter((file)=>file.t === fileType).map((file)=>toMediaSrcFile(deviation, file));
    }
    getSrcSetForDeviationPreview(deviation, isMobile, screenVw) {
        // Use thumbs etc that do not crop
        const thumbTypes = [
            '375W',
            '414W',
            '750W',
            '828W'
        ];
        // Get all available images, excluding fullview
        const images = this._getFilteredMediaTypes(deviation, {
            allowFullview: false,
            allowPreview: true,
            thumbTypes
        });
        // Use the fullview as src, the srcset will make this not render
        const isGif = this.isGif(deviation);
        const isPng = this.isPng(deviation);
        const src = this.getLargestFullview(deviation);
        const preview = this.getDeviationFile(deviation, 'preview');
        const fullview = this.getDeviationFile(deviation, 'fullview');
        if (!src || !preview) {
            throw new Error('Cannot render a fullview without a fullview image');
        }
        // GIF is special, we don't know if they are animated so need to use the fullview
        // however transparent gifs will get all gif thumbs/preview so when that happens
        // we don't apply this special handling
        // return attribs so that we end up building empty srcset
        if (isGif && this._doesGifHaveStaticPreview(deviation)) {
            return {
                sizes: '',
                src: src.src,
                srcSet: `${preview.src} 10000w`,
                srcFile: src,
                previewFile: preview
            };
        }
        // Special handling of PNGS
        // Some pixel artists use tiny apng and like gifs we have no idea
        // if the png is animated or not and pngs can be very very large
        // so we cap to ~200k and use static previews after that.
        if (isPng && fullview && fullview.filesize && fullview.filesize < 200 * 1000) {
            return {
                sizes: '',
                src: src.src,
                srcSet: `${fullview.src} 10000w`,
                srcFile: src,
                previewFile: fullview
            };
        }
        // First grab all the embedded srcsets from the media files into
        // a flat array so we can include all the 2x versions
        const flatSet = [];
        for (const _ref of images){
            let img = _ref;
            const { ss } = img, rest = _object_without_properties(img, [
                "ss"
            ]);
            flatSet.push(rest);
            if (ss && Array.isArray(ss) && ss.length > 0) {
                for (const srcItem of ss){
                    flatSet.push(_object_spread_props(_object_spread({}, rest), {
                        t: `${rest.t}_x${srcItem.x}`,
                        w: srcItem.w,
                        h: srcItem.h,
                        c: srcItem.c
                    }));
                }
            }
        }
        const srcSet = flatSet// Dedupe by width
        .filter((v, i, a)=>a.findIndex((v2)=>v2.w === v.w) === i)// Sort small to large
        .sort((a, b)=>{
            const asc = (a.w || 0) * (a.h || 0);
            const bsc = (b.w || 0) * (b.h || 0);
            if (asc === bsc) {
                return 0;
            }
            return asc < bsc ? -1 : 1;
        })// Convert to media file
        .map((file)=>toMediaSrcFile(deviation, file))// Create the srcset strings
        .map((mediaFile, index, stack)=>{
            // Last item gets the src width * 2, this hopefully forces browser
            // to choose from the srcset
            if (stack[index + 1] === undefined) {
                return `${mediaFile.src} ${(src.width || 0) * 2}w`;
            }
            return `${mediaFile.src} ${mediaFile.width || 0}w`;
        });
        var _src_src;
        return {
            sizes: screenVw,
            src: (_src_src = src.src) !== null && _src_src !== void 0 ? _src_src : '',
            srcSet: srcSet.join(', '),
            srcFile: src,
            previewFile: preview
        };
    }
    _getLargestFullview(deviation) {
        const fvs = this._getFullviews(deviation);
        if (fvs.length === 0) {
            return defaultFullview;
        }
        const fv = fvs.filter((item)=>item.t !== 'social_preview').reduce((prev, curr)=>{
            if ((prev.w || 0) > (curr.w || 0) || (prev.h || 0) > (curr.h || 0)) {
                return prev;
            }
            return curr;
        });
        return fv && toMediaSrcFile(deviation, fv) || defaultFullview;
    }
    _getFilteredMediaTypes(deviation, options) {
        let thumbFileTypes = [
            '150',
            '200H',
            '300W',
            '200T',
            '250T',
            '350T',
            '400T',
            '375W',
            '414W',
            '750W',
            '828W'
        ];
        if (options === null || options === void 0 ? void 0 : options.thumbTypes) {
            thumbFileTypes = options.thumbTypes;
        }
        // Check if this deviation may be compressed by the editor, in this
        // case we can have reduced files array and need to allow
        if (this._mightBeCompressedFilesList(deviation)) {
            thumbFileTypes.push('preview', 'fullview', 'gif');
        }
        if (options && options.allowPreview) {
            thumbFileTypes.push('preview');
        }
        if (options && options.allowFullview) {
            thumbFileTypes.push('fullview');
            thumbFileTypes.push('fullview_unpublished');
        }
        if (options && options.allowSquareCrop) {
            thumbFileTypes.push('92S');
            thumbFileTypes.push('125S');
        }
        if (!deviation || !deviation.media || !deviation.media.types) {
            return [];
        }
        return deviation.media.types.filter((file)=>thumbFileTypes.includes(file.t));
    }
    _doesGifHaveStaticPreview(deviation) {
        const thumb = this.getDeviationFile(deviation, '250T');
        return thumb && !/\.gif\?/.test(thumb.src);
    }
}
export default MediaHandler;

//# sourceMappingURL=MediaHandler.js.map